import { useState, useEffect } from "react";
import { api, errMsg, readFilesAsDataURLs } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Image as ImageIcon } from "@phosphor-icons/react";
import { AiSuggest } from "@/components/AiSuggest";
import { useCurrencySymbol } from "@/hooks/useCurrencySymbol";

const EMPTY = {
  name: "", description: "", sku: "", category: "", client_name: "",
  price: 0, currency: "INR", weight_grams: 0, egg_type: "eggless", diet_type: "veg", images: [],
};

export default function ProductForm({ open, onOpenChange, product, onSaved }) {
  const currencySymbol = useCurrencySymbol();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(product ? { ...EMPTY, ...product } : EMPTY);
  }, [open, product]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const addImages = async (e) => {
    const urls = await readFilesAsDataURLs(e.target.files);
    setForm((f) => ({ ...f, images: [...f.images, ...urls].slice(0, 8) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, weight_grams: Number(form.weight_grams) || 0 };
      if (product) {
        await api.patch(`/products/${product.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created — 23-step pipeline initialized");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">
            {product ? "Edit Product" : "New Product"}
          </DialogTitle>
          <DialogDescription>
            {product ? "Update product details." : "Create a product — its 23-step pipeline starts automatically."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="product-form">
          <div className="space-y-1.5">
            <Label>Product name *</Label>
            <Input data-testid="product-name-input" required value={form.name} onChange={set("name")} placeholder="Sourdough Loaf 500g" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Description</Label>
              <AiSuggest
                kind="product"
                context={{ name: form.name, category: form.category, egg_type: form.egg_type, diet_type: form.diet_type }}
                onApply={(item) => setForm((f) => ({ ...f, description: item.value, category: item.extra?.category || f.category }))}
                testid="ai-product-suggest"
              />
            </div>
            <Textarea data-testid="product-description-input" value={form.description} onChange={set("description")} rows={2} placeholder="Short description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>SKU</Label>
              <Input data-testid="product-sku-input" value={form.sku} onChange={set("sku")} placeholder="BF-001" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input data-testid="product-category-input" value={form.category} onChange={set("category")} placeholder="Breads" />
            </div>
            <div className="space-y-1.5">
              <Label>Client</Label>
              <Input data-testid="product-client-input" value={form.client_name} onChange={set("client_name")} placeholder="Client name" />
            </div>
            <div className="space-y-1.5">
              <Label>Weight (g)</Label>
              <Input data-testid="product-weight-input" type="number" min="0" value={form.weight_grams} onChange={set("weight_grams")} />
            </div>
            <div className="space-y-1.5">
              <Label>Price ({currencySymbol})</Label>
              <Input data-testid="product-price-input" type="number" min="0" step="0.01" value={form.price} onChange={set("price")} />
            </div>
            <div className="space-y-1.5">
              <Label>Egg type</Label>
              <Select value={form.egg_type} onValueChange={(v) => set("egg_type")(v)}>
                <SelectTrigger data-testid="product-egg-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eggless">Eggless</SelectItem>
                  <SelectItem value="egg">With Egg</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Diet type</Label>
              <Select value={form.diet_type} onValueChange={(v) => set("diet_type")(v)}>
                <SelectTrigger data-testid="product-diet-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="veg">Vegetarian</SelectItem>
                  <SelectItem value="non_veg">Non-Vegetarian</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Product images</Label>
            <div className="flex flex-wrap gap-2">
              {form.images.map((img, i) => (
                <div key={i} className="relative h-16 w-16">
                  <img src={img} alt="" className="h-16 w-16 rounded-md border border-slate-200 object-cover" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 shadow border border-slate-200">
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-400 hover:border-[#EA580C] hover:text-[#EA580C] transition-colors" data-testid="product-image-upload">
                <ImageIcon size={20} />
                <input type="file" accept="image/*" multiple className="hidden" onChange={addImages} />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="product-save-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
              {saving ? "Saving…" : product ? "Save changes" : "Create product"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
